import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

import AgendaTech from '../components/Agenda/AgendaTech';
import AgendaApp from '../components/Agenda/AgendaApp';
import { Name } from 'pdfjs-dist/build/pdf.worker';

import useUsers from "../hooks/useUsers";
import useFetchDeclarations from '../hooks/useFetchDeclarations';
import useFetchSites from '../hooks/useFetchSites';
import { useAuth } from '../contexts/AuthContext';

import config from '../config/config';
import authHeader from '../config/authHeader';

function Agenda() {
    const location = useLocation();
    const mode = location.state?.mode ?? 'team';

    const now = new Date();
    const todayAt = (h, m = 0) => new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, m, 0);

    const isZeroDate = (s) => !s || s === "0000-00-00";
    const pad2 = (n) => String(n).padStart(2, "0");
    const secToHM = (sec = 0) => ({ h: Math.floor(sec / 3600), m: Math.floor((sec % 3600) / 60) });
    const makeDateTime = (ymd, h, m) =>
        new Date(`${ymd}T${pad2(h)}:${pad2(m)}:00`);

    const [loadingRDV, setLoadingRDV] = useState(true);
    const [lstRdv, setLstRdv] = useState([]);

    const { users, fetchUsers } = useUsers();
    const { declarations, fetchDeclarations } = useFetchDeclarations();
    const { sites, fetchSites } = useFetchSites();

    useEffect(() => {
        fetchUsers();
    }, []);
   
    useEffect(() => {
        fetchDeclarations();
    }, []);

    useEffect(() => {
        fetchSites();
    }, []);


    useEffect(() => {
        setLoadingRDV(true);

        fetch(`${config.apiBaseUrl}/4DACTION/KST_getExpertises`, {
            method: 'GET',
            headers: authHeader()
        })
            .then((response) => response.json())
            .then((data) => {
                setLstRdv(data);
            })
            .catch((error) => {
                console.error("Erreur lors de la récupération des rendez-vous:", error);
                setLoadingRDV(false);
            });

        setLoadingRDV(false);
    }, []);

    const USERS = useMemo(() => {
            return (users ?? [])
                //.slice(0, 5)
                .map((u, idx) => ({
                    Name: `${u?.Nom ?? ""}`.trim() || "Sans nom",
                    UserUUID: String(u?.UUID_),
                    Color: u?.color ?? "#FF5733",
                    Role: u?.role ?? "",
                    Charge_Affaire: Boolean(u?.Charge_Affaire)
                }));
    }, [users]);

    const findUserIdByCA = useCallback((ca) => {
        if (!USERS.length) return undefined;
        if (!ca) return USERS[0].UserUUID;

        const firstBloc = ca.split("-")[0].trim().toLowerCase();
        const firstNameToken = firstBloc.split(/\s+/)[0];

        const match = USERS.find(u => {
            const fullName = `${u?.Name ?? ""}`.toLowerCase();
            return fullName.includes(firstBloc) || fullName.includes(firstNameToken);
        });

        return match?.UserUUID ?? USERS[0].UserUUID;
    }, [USERS]);

    const EVENTS = useMemo(() => {
        if (!Array.isArray(lstRdv)) return [];
console.log("rdv",lstRdv)

        const ONE_HOUR = 60 * 60 * 1000;

        const parseEnd = (rdv, start, ymd) => {
            // Prefer Date_fin if valid, else fall back to same date as start
            const endYmd = !isZeroDate(rdv?.Date_fin) ? rdv.Date_fin : ymd;

            // Heure_fin is seconds (e.g. 36000 => 10:00)
            const { h: eh = NaN, m: em = NaN } = secToHM(rdv?.Heure_fin ?? 0);

            let end;
            if (endYmd && Number.isFinite(eh) && Number.isFinite(em) && rdv?.Heure_fin) {
                end = makeDateTime(endYmd, eh, em);
            } else {
                end = new Date(start.getTime() + ONE_HOUR);
            }

            // Guard: ensure end is after start
            if (!(end instanceof Date) || isNaN(end) || end <= start) {
                end = new Date(start.getTime() + ONE_HOUR);
            }
            return end;
        };

        return lstRdv.map((rdv, idx) => {
            const ymd =
                !isZeroDate(rdv?.Date_Convoc) ? rdv.Date_Convoc :
                    !isZeroDate(rdv?.Date_Rdv) ? rdv.Date_Rdv :
                        null;

            const { h, m } = secToHM(rdv?.Heure_Convoc || rdv?.Heure_Rdv || 3600 * 9);
            const start = ymd ? makeDateTime(ymd, h, m) : todayAt(9, 0);

            const end = parseEnd(rdv, start, ymd);

            return {
                Id: rdv?.UUID_ || `tmp-${idx}`,
                Subject: rdv?.Titre || rdv?.Type_Document || "Rendez-vous",
                StartTime: start,
                EndTime: end,
                Description: rdv?.Resume || "",
                Location: rdv?.Four_Nom || "",
                UserUUID: findUserIdByCA(rdv?.CA),
                declarationUUID:rdv.UUID_Declaration,
                siteUUID:rdv.UUID_Site_
            };
        });
    }, [lstRdv, findUserIdByCA]);


    //console.log("EVENTS passed to AgendaTech:", EVENTS);

    /*Gestion utilisateur connecté */
    const { user } = useAuth();
    const userUUID = user?.UUID_;
    const USERS_NORM = (USERS ?? []).map(u => ({ ...u, UserUUID: String(u.UserUUID) }));
    const EVENTS_NORM = (EVENTS ?? []).map(e => ({ ...e, UserUUID: String(e.UserUUID) }));
    const filteredUsers = mode == 'personal' ? USERS_NORM.filter(u => u.UserUUID === String(userUUID)) : USERS_NORM;
    const filteredEvents = mode == 'personal' ? EVENTS_NORM.filter(e => e.UserUUID === String(userUUID)) : EVENTS_NORM;
    //console.log("Filtered EVENTS for mode", mode, ":", filteredEvents);
    //console.log("Filtered USERS for mode", mode, ":", filteredUsers);
    /* const userIdsWithEvents = new Set(EVENTS.map(e => e.UserUUID));
     const filteredUsers = USERS.filter(u => userIdsWithEvents.has(u.UserUUID));*/

    return (
        <>
            <div class="z-10 flex-1 p-10">
                <div class="gap-6 grid grid-cols-12">
                    <div class="gap-6 grid grid-cols-12 col-span-12 xxl:col-span-9">
                        <div class="col-span-12 mt-8">
                            <h1 class="mb-10 w-full font-bold text-gray-800 text-4xl text-center">Agenda</h1>
                            <div className="bg-white p-4 rounded-lg">
                                <AgendaTech users={filteredUsers} events={filteredEvents} sites={sites} declarations={declarations} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default Agenda;